/**
 * 宣传特访系统（2026-10-31 批，设计 canonical = docs/玩法设计.md §综艺「常驻宣传综艺」）。
 *
 * 宣传邀约体系=四档常规综艺（《周末openmic》/《大驾光临》/《星动之夜》/《追更营业中》，
 * SCRIPT_170~174 已导码进 variety.ts）+两种宣发行程（路演/媒体专访）：固定 MC 只收飞行嘉宾、
 * **宣传特访型**——⛔ 不入试镜池、不走月掷；触发=玩家以一番身份的剧集杀青
 * （ScriptSystem.completeShoot 调 maybeOfferOnWrap）**掷骰递约**（2026-10-31 茄子令「必得不合理」）：
 * 递约率 = 基础 20% × 质量加成（baseScore 7→×1.5、8→×2，头部剧才有宣发预算）× 正剧负向（×0.5），
 * clamp 90%；战争/谍战/历史题材**直接不上**（硬闸）。
 *
 * 选形态（同日定稿「撤剧种锚定+加路演/专访」）：掷中后从资格池随机挑——粉丝向题材（爱情/偶像/
 * 仙侠/校园/音乐/奇幻）→ 咖位 ≤6 开麦 / 7+ 星动；大驾收一切；追更仅网剧（平台联动档）；
 * 路演仅电影；专访仅偏正剧向。
 * ⛔ 不与节目自身 themes 求交（那是节目类型不是嘉宾画像）。
 * 同节目冷却 12 个月：录制月结盖章，到期前该形态不再递；放弃邀约不算上过（无全局冷却）。
 *
 * 职责切分（仿 EndorsementSystem 头注释惯例）：本系统只管「触发+合约账本」——offer 生成/签约/放弃/
 * 录制月月结结算；呈现不在这里（送达链=经纪人 VN+重档弹窗走 app.ts deliverStep；到周自动赴约 VN 走
 * MonthProgressPanel，账由 monthlySettle 结，VN 是呈现层）。非品牌合作：无排他、无官宣、无续约；
 * **放弃=不拉黑**（机会型，下部剧杀青还会来）。
 *
 * 占周同尺：签约进 player.varietyGuests，与挚友品牌活动一起由 EndorsementSystem.occupiedEventDetail
 * 集中出口（SocialSystem.isWeekOccupied / CharactersPanel 周选共用同一把尺，⛔ 另算第二套）。
 */
import { GameConfig } from '../core/GameConfig';
import { GameData } from '../data/GameData';
import { PendingVarietyOffer, ScriptDef, VarietyGuestBooking } from '../data/types';
import { GameState } from './GameState';
import { endorsementSystem } from './EndorsementSystem';

/** 月结产出的待通报条目（录制完成回执；UI 决策页消费后弹小助理 VN——剧宣归助理域） */
export interface VarietyNotice {
    /** 小助理口吻的完整通报句（~ 记号由 UI joinTerm 拼昵称；已含金额插值） */
    text: string;
}

class VarietyPromoSystem {
    private static instance: VarietyPromoSystem;
    static getInstance(): VarietyPromoSystem {
        if (!VarietyPromoSystem.instance) VarietyPromoSystem.instance = new VarietyPromoSystem();
        return VarietyPromoSystem.instance;
    }
    private constructor() { }

    /** 月结产出的待通报队列（录制完成回执）；UI 消费一条 shift 一条 */
    readonly noticeQueue: VarietyNotice[] = [];

    private absMonth(): number {
        const p = GameState.getInstance().player;
        return p.year * 12 + p.month;
    }

    /**
     * 选宣传形态（2026-10-31 茄子定稿「撤锚定+加路演/专访」）：从该剧的**资格池**随机挑一种
     * （未被冷却锁的）。资格：
     *   - 《周末openmic》：粉丝向题材 ∧ 咖位 ≤6
     *   - 《星动之夜》：粉丝向题材 ∧ 咖位 7+
     *   - 《大驾光临》：无条件（访谈收一切）
     *   - 《追更营业中》：**仅网剧**（平台剧综联动档，2026-10-31 茄子令）
     *   - 路演：**仅电影**（映前路演是电影宣发转化最直接的方式）
     *   - 媒体专访：**仅偏正剧向**（非粉丝向——被粉丝向综艺排除的那批剧，现实里正剧宣发走深度访谈）
     * 硬闸题材（战争/谍战/历史）在 promoRateOf 恒 0，走不到这里。
     * @returns formId 或 null（资格池全被冷却锁住=本部错过，机会型）
     */
    pickForm(script: ScriptDef): string | null {
        const p = GameState.getInstance().player;
        const cooled = (id: string): boolean => this.absMonth() <= (p.varietyShowCooldowns?.[id] ?? 0);
        const fan = script.themes?.some(t => GameConfig.VARIETY_PROMO_FAN_THEMES.includes(t)) ?? false;
        const candidates: string[] = [];
        if (fan && p.rank <= GameConfig.VARIETY_PROMO_FAN_CUT_RANK) candidates.push('SCRIPT_170');
        if (fan && p.rank > GameConfig.VARIETY_PROMO_FAN_CUT_RANK) candidates.push('SCRIPT_172');
        candidates.push('SCRIPT_171');                                   // 大驾收一切
        if (script.genre === 'web') candidates.push('SCRIPT_174');       // 追更仅限网剧（平台联动档）
        if (script.genre === 'movie') candidates.push('PROMO_ROADSHOW'); // 路演仅限电影
        if (!fan) candidates.push('PROMO_INTERVIEW');                    // 专访仅限偏正剧向（非粉丝向）
        const open = candidates.filter(id => !cooled(id) && !!GameConfig.VARIETY_PROMO[id]);
        if (!open.length) return null;
        return open[Math.floor(Math.random() * open.length)];
    }

    /**
     * 宣传综艺递约率（2026-10-31 茄子令「必得不合理」改掷骰）：基础率 × 质量加成 × 正剧负向，clamp 上限。
     * 质量按杀青时剧的 baseScore（头部剧才有宣发预算）；战争/谍战/历史=硬闸恒 0（茄子令「直接不上综艺」）。
     */
    promoRateOf(script: ScriptDef): number {
        if (script.themes?.some(t => GameConfig.VARIETY_PROMO_BAN_THEMES.includes(t))) return 0;
        let rate = GameConfig.VARIETY_PROMO_BASE_PCT;
        if (script.baseScore >= GameConfig.VARIETY_PROMO_QUALITY_TOP_LINE) rate *= GameConfig.VARIETY_PROMO_QUALITY_TOP_MULT;
        else if (script.baseScore >= GameConfig.VARIETY_PROMO_QUALITY_LINE) rate *= GameConfig.VARIETY_PROMO_QUALITY_MULT;
        if (script.themes?.some(t => GameConfig.VARIETY_PROMO_SERIOUS_THEMES.includes(t))) rate *= GameConfig.VARIETY_PROMO_SERIOUS_MULT;
        return Math.min(rate, GameConfig.VARIETY_PROMO_PCT_CAP);
    }

    // ===== 触发（一番杀青掷骰递约） =====

    /**
     * 番剧杀青触发（ScriptSystem.completeShoot 调用）：一番 && 非综艺 && 硬闸题材外 && 掷骰命中 &&
     * 通道空闲 && 待播宣传月有空周 → 生成 pendingVarietyOffer。任何一条不满足=安静放过
     * （机会型，不排队不补发——掷不中=这一部错过，下部剧再来）。
     * @param premiereAbs 待播宣传月（=开播月，dramaTimelineOf().premiere 口径）；玩家拖期杀青时可能
     *   已过——定档兜底 max(premiere, 下月)，录制永远落在未来周
     */
    maybeOfferOnWrap(script: ScriptDef, roleBilling: number | undefined, premiereAbs: number): PendingVarietyOffer | null {
        const state = GameState.getInstance();
        const p = state.player;
        if (script.genre === 'variety') return null;      // 综艺不宣综艺
        if (script.genre === 'short') return null;        // 短剧不上综艺（2026-10-31 茄子令；现实短剧宣发也不走综艺）
        if (roleBilling !== 1) return null;               // 只认一番
        const formId = this.pickForm(script);
        if (!formId || !GameConfig.VARIETY_PROMO[formId]) return null;   // 资格池全被冷却锁住=本部错过
        const rate = this.promoRateOf(script);
        if (rate <= 0 || Math.random() >= rate) return null;   // 掷骰不中（含硬闸恒 0）=这一部错过
        if (p.pendingVarietyOffer) return null;           // 通道被占：机会型不排队（错过=下部剧再来）
        const eventMonth = Math.max(premiereAbs, this.absMonth() + 1);
        const conf = GameConfig.VARIETY_PROMO[formId];
        const show = GameData.SCRIPT_MAP[formId];         // 综艺档才有剧本条目；路演/专访为 undefined
        const formName = show?.name ?? conf.label;
        // 全形态统一不带周（2026-10-31 茄子令：剧宣行程都走「小助理通知→选周→到点演出」，
        // 剧组请你配合宣传的口径；周由选周弹窗落定，撞周在 accept 兜底）
        p.pendingVarietyOffer = {
            showId: formId,
            scriptId: script.id,
            fee: conf.fee,
            eventMonth,
            month: this.absMonth(),
            brokerLine: this.brokerLine(formId, formName, script.name),
        };
        return p.pendingVarietyOffer;
    }

    /**
     * 递约台词（2026-10-31 茄子令：全形态统一小助理口吻＋「剧组请你配合宣传」口径——剧宣是剧方的
     * 宣发任务，不是节目方商务邀约）。**canonical 文案源 = docs/夏夏台词-文案填写表.md §14~19**
     * （初稿待茄子改，改后导码回此处）；行首 `~` 记号=渲染时 assistantSystem.joinTerm 拼昵称。
     * 信息复述归选周弹窗，⛔ 台词念报价。
     */
    private brokerLine(formId: string, formName: string, dramaName: string): string {
        const tpl: Record<string, string> = {
            SCRIPT_170: `~《{剧名}》要开播了，剧组打算安排你去《周末openmic》当一期飞行嘉宾。你看排宣传月哪一周？`,
            SCRIPT_171: `~《{剧名}》要开播了，剧组想安排你带作品去《大驾光临》做一期主宾。你想排在哪一周？`,
            SCRIPT_172: `~《{剧名}》开播在即，剧组打算安排你上《星动之夜》的特辑，舞台竞演排面拉满。你看排哪一周？`,
            SCRIPT_174: `~《{剧名}》开播在即，剧组打算安排你去《追更营业中》进棚和追更团玩玩梗。你看排哪一周？`,
            PROMO_ROADSHOW: `~《{剧名}》定档了，剧组安排了几场映前路演。哪一周方便？我帮你排进行程。`,
            PROMO_INTERVIEW: `~有一家主流媒体想约你做《{剧名}》开播前的深度专访。你看排宣传月哪一周？`,
        };
        return (tpl[formId] ?? `{剧名}下月开播，《${formName}》递来一期飞行嘉宾的通告。`).replace('{剧名}', dramaName);
    }

    // ===== 签约 / 放弃（两键强制二选一；UI 从 pendingVarietyOffer 进入） =====

    /** 当前待签邀约（UI 渲染重档弹窗用；无 pending 返回 null）。formName=形态显示名（节目名/路演/媒体专访） */
    currentOffer(): { offer: PendingVarietyOffer; formName: string } | null {
        const offer = GameState.getInstance().player.pendingVarietyOffer;
        if (!offer) return null;
        const conf = GameConfig.VARIETY_PROMO[offer.showId];
        if (!conf) return null;
        const show = GameData.SCRIPT_MAP[offer.showId];
        return { offer, formName: show?.name ?? conf.label ?? offer.showId };
    }

    /**
     * 签约：落录制通告（占自由日周）→ 回执句。⛔ 当场结钱/名气——录制还没发生，全挂录制月月结。
     * 全形态统一选周路径（2026-10-31 茄子令）：周由选周弹窗传入，撞周（递约后玩家又排了别的）留弹原因。
     */
    accept(week?: number): { ok: boolean; detail?: string } {
        const p = GameState.getInstance().player;
        const offer = p.pendingVarietyOffer;
        if (!offer) return { ok: false, detail: '没有待排的宣传行程。' };
        if (!week || week < 1 || week > 4) return { ok: false, detail: '还没选行程周。' };
        if (this.weekOccupied(offer.eventMonth, week)) return { ok: false, detail: '那一周已经有安排了，换一周吧。' };
        const booking: VarietyGuestBooking = {
            id: `VG_${offer.showId}_${offer.month}`,
            showId: offer.showId,
            scriptId: offer.scriptId,
            eventMonth: offer.eventMonth,
            eventWeek: week,
            fee: offer.fee,
            active: true,
        };
        p.varietyGuests = [...(p.varietyGuests ?? []), booking];
        p.pendingVarietyOffer = null;
        const showName = GameData.SCRIPT_MAP[offer.showId]?.name ?? offer.showId;
        const conf = GameConfig.VARIETY_PROMO[offer.showId];
        const what = conf && conf.fee > 0 ? '通告' : '行程';
        return { ok: true, detail: `《${showName}》的${what}排好了，第${offer.eventMonth}月第${week}周，费用月底随月结到账。` };
    }

    /** 指定月该周是否被定档活动占用（品牌活动/综艺录制/其他行程——统一 occupiedEventWeeks 尺） */
    private weekOccupied(month: number, week: number): boolean {
        return endorsementSystem.occupiedEventWeeks(month).includes(week);
    }

    /** 放弃：只清通道（**不拉黑**——宣传邀约是机会型，下部剧杀青还会来） */
    reject(): { ok: boolean; detail?: string } {
        const p = GameState.getInstance().player;
        if (!p.pendingVarietyOffer) return { ok: false, detail: '没有待签的综艺邀约。' };
        p.pendingVarietyOffer = null;
        return { ok: true, detail: '行，这次就不去了——下部戏杀青，这类通告还会来。' };
    }

    // ===== 月结（GameLoop doFinishMonth 内、endorsementSystem.monthlySettle 同窗调用） =====

    /**
     * 录制月月结自动结算（月结兜底粒度，与挚友活动同模型；到周的 VN 呈现归 MonthProgressPanel）：
     * 通告费终值到账 + 作品 heat 入 heatPromoted 账本（与剧宣/客串/好友剧宣**共用账本**，
     * 封顶在读取端 workHeatOf 的 min——这里只管记账，⛔ 读取端另设第二套上限）+ 名气到账 + 通报回执。
     */
    monthlySettle(abs: number): void {
        const state = GameState.getInstance();
        const p = state.player;
        for (const v of p.varietyGuests ?? []) {
            if (!v.active || v.eventMonth > abs) continue;
            v.active = false;
            state.addMoney(v.fee);
            const conf = GameConfig.VARIETY_PROMO[v.showId];
            if (conf) {
                p.heatPromoted = { ...(p.heatPromoted ?? {}), [v.scriptId]: (p.heatPromoted?.[v.scriptId] ?? 0) + conf.heat };
                state.addFame(conf.fame);
            }
            // 同节目冷却盖章（2026-10-31 茄子令「现实不会一年上同一综艺两次」）：从录制月起算 N 个月，
            // 到期前该节目不再递；放弃邀约不盖章（没上过节目）
            p.varietyShowCooldowns = {
                ...(p.varietyShowCooldowns ?? {}),
                [v.showId]: v.eventMonth + GameConfig.VARIETY_PROMO_SHOW_COOLDOWN_MONTHS,
            };
            // 回执＝小助理口吻（2026-11-01 茄子令「经纪人 VN 太尬」：剧宣归助理域，⛔ 台词念「加热」这类机制话）
            this.noticeQueue.push({ text: this.settleLine(v.fee, v.showId) });
        }
    }

    /** 录完回执句（小助理口吻；实机反馈 2026-11-01「经纪人念加热太尬」——机制话⛔ 回台词） */
    private settleLine(fee: number, showId: string): string {
        const showName = GameData.SCRIPT_MAP[showId]?.name ?? showId;
        if (showId === 'PROMO_ROADSHOW') return `~路演跑完了，场场都坐得挺满的。`;
        if (showId === 'PROMO_INTERVIEW') return `~专访登出来了，评论区讨论得挺热闹。`;
        return `~《${showName}》录完了，通告费${wan(fee)}到账啦。`;
    }

    /** 消费一条待通报（决策页入场时轮询；空返回 null） */
    consumeNotice(): VarietyNotice | null {
        return this.noticeQueue.shift() ?? null;
    }

    // ===== 到周自动赴约 VN（呈现层数据；账在 monthlySettle，这里只出词） =====

    /**
     * 该周到点的录制通告（MonthProgressPanel.enterWeekend 调用；互斥下正常 0~1 条，坏档多条取最先挂的）。
     * @param week 本月第几周（执行页 current.week 口径）
     */
    bookingDueAt(abs: number, week: number): VarietyGuestBooking | null {
        return (GameState.getInstance().player.varietyGuests ?? [])
            .find(v => v.active && v.eventMonth === abs && v.eventWeek === week) ?? null;
    }

    /** 行程赴约 VN 两句：现场旁白 + 小助理事后一句（2026-11-01 茄子令「经纪人念加热太尬」——剧宣归助理域，
     *  ⛔ 台词念「加热」这类机制话；路演/专访 0 元⛔ 说「费用到账」）。按形态分三档 */
    recordVnLines(booking: VarietyGuestBooking): { narrator: string; followup: string } {
        const conf = GameConfig.VARIETY_PROMO[booking.showId];
        const formName = conf?.label || (GameData.SCRIPT_MAP[booking.showId]?.name ?? booking.showId);
        if (booking.showId === 'PROMO_ROADSHOW') {
            return {
                narrator: `你连跑了三座城市的映前见面会，影厅里坐得满满当当，映后的掌声是最直接的反馈。`,
                followup: `~路演收工啦，宣传这一轮稳了。`,
            };
        }
        if (booking.showId === 'PROMO_INTERVIEW') {
            return {
                narrator: `你接受了一家主流媒体的深度专访，从入行聊到这部作品，说了很多镜头前没说过的东西。`,
                followup: `~专访收工啦，就等播出了。`,
            };
        }
        return {
            narrator: `你走进${formName}的录制棚，游戏、访谈一轮下来，要说的话都递到了。`,
            followup: `~录得挺顺的，通告费月底随月结到账。`,
        };
    }
}

/** 金额→「x万」展示（与 EndorsementSystem 同式；万元取整到一位小数，<1万 显示元） */
function wan(n: number): string {
    if (n < 10000) return `${n}元`;
    const w = n / 10000;
    const text = Number.isInteger(w) ? String(w) : w.toFixed(1);
    return `${text}万`;
}

/** 导出单例 */
export const varietyPromoSystem = VarietyPromoSystem.getInstance();
